import { Head } from "zudoku/components";
import { Link, useLocation } from "react-router";

import { SITE_URL } from "./urls.generated.mjs";

/**
 * Sélecteur de langue — français à la racine, anglais sous `/en/`.
 *
 * Même convention que le site vitrine : les slugs sont identiques dans les deux
 * langues, seul le préfixe change. Le sélecteur conserve donc la page courante.
 *
 * Il porte aussi les `hreflang` et l'attribut `lang` de la page : Zudoku n'a pas
 * d'internationalisation native, ces balises n'existeraient pas autrement.
 */

/** La référence d'API est générée depuis la spec : une seule version, non traduite. */
const isReference = (path: string) => path === "/api" || path.startsWith("/api/");

export const toFrenchPath = (path: string) =>
  path.startsWith("/en/") ? path.slice(3) : path === "/en" ? "/" : path;

export const toEnglishPath = (path: string) =>
  path.startsWith("/en/") || path === "/en" ? path : `/en${path}`;

const LanguageLink = ({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) => (
  <Link
    to={to}
    hrefLang={to.startsWith("/en") ? "en" : "fr"}
    aria-current={active ? "true" : undefined}
    className={
      active
        ? "rounded-sm bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground"
        : "rounded-sm px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
    }
  >
    {children}
  </Link>
);

export const LangSwitcher = () => {
  const { pathname } = useLocation();
  const isEnglish = pathname === "/en" || pathname.startsWith("/en/");

  const frenchPath = toFrenchPath(pathname);
  // Sur la référence, les deux langues pointent sur la même page.
  const englishPath = isReference(pathname)
    ? pathname
    : toEnglishPath(frenchPath);

  return (
    <>
      <Head>
        <html lang={isEnglish ? "en" : "fr"} />
        {!isReference(pathname) && (
          <link rel="alternate" hrefLang="fr" href={`${SITE_URL}${frenchPath}`} />
        )}
        {!isReference(pathname) && (
          <link
            rel="alternate"
            hrefLang="en"
            href={`${SITE_URL}${englishPath}`}
          />
        )}
        {!isReference(pathname) && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${SITE_URL}${frenchPath}`}
          />
        )}
      </Head>

      <nav
        aria-label={isEnglish ? "Language" : "Langue"}
        className="flex items-center gap-0.5 rounded-md border border-border p-0.5"
      >
        <LanguageLink to={frenchPath} active={!isEnglish}>
          FR
        </LanguageLink>
        <LanguageLink to={englishPath} active={isEnglish}>
          EN
        </LanguageLink>
      </nav>
    </>
  );
};
